import cn from "classnames";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { Input } from "@/components/Input";
import { Tooltip } from "@/components/Tooltip";
import { ROUTES } from "@/routes";

import { Loading } from "../Loading";
import { Text } from "../Text";

import styles from "./LoginForm.module.scss";

interface LoginFormProps {
  containerClassName?: string;
  error?: string | null;
  onSubmit: (_: LoginFormData) => void;
}

export interface LoginFormData {
  login: string;
  password: string;
}

export const LoginForm = function LoginForm(props: LoginFormProps) {
  const { containerClassName, error, onSubmit } = props;

  const { t } = useTranslation();

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<LoginFormData>();

  const containerClasses = cn(styles.container, containerClassName);

  return (
    <form className={containerClasses} onSubmit={handleSubmit(onSubmit)}>
      <Input
        type="email"
        placeholder={t("Auth:EnterEmail") ?? ""}
        error={errors.login?.message}
        autoFocus
        {...register("login", {
          required: {
            value: true,
            message: t("Auth:EmailRequiredError"),
          },
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: t("Auth:EmailWrongFormatError"),
          },
        })}
      />
      <div className={styles["input-container"]}>
        <Input
          type="password"
          placeholder={t("Auth:EnterPassword") ?? ""}
          error={errors.password?.message}
          {...register("password", {
            required: {
              value: true,
              message: t("Auth:PasswordRequiredError"),
            },
          })}
        />

        <Link href={ROUTES.RESET}>
          <Text variant="text-2-link" color="accsent-3s">
            {t("Auth:ForgotPassword")}
          </Text>
        </Link>
      </div>

      {error && (
        <Text variant="text-2-semibold" color="accsent-wd">
          {error}
        </Text>
      )}

      <Checkbox checked>
        <Text variant="text-2-regular" color="base-d">
          {`${t("Auth:AgreeWith")} `}
          <a
            href="https://govoriru.ru/doc/polojenie_privacy.pdf"
            rel="noopener noreferrer"
            target="_blank"
          >
            <Text variant="text-2-link" color="accsent-3s">
              {t("Auth:ProcessingPersonalData")}
            </Text>
          </a>
        </Text>
      </Checkbox>
      <Checkbox checked>
        <Text variant="text-2-regular" color="base-d">
          {`${t("Auth:AgreeWith")} `}
          <a
            href="https://govoriru.ru/doc/polzovatelskoe_soglashenie.pdf"
            rel="noopener noreferrer"
            target="_blank"
          >
            <Text variant="text-2-link" color="accsent-3s">
              {t("Auth:UserAgreement")}
            </Text>
          </a>
        </Text>
      </Checkbox>

      <Button type="submit">
        <Loading loading={isSubmitting}>{t("Login:Login")}</Loading>
      </Button>
      <Button
        link={`${ROUTES.REGISTER}?promocode=Ph-6oD5_jqnehUZNVKtaGg&utm_campaign=4-RKI_LO-110632`}
        variant="soft"
      >
        {t("Login:Register")}
      </Button>
      <Tooltip title={t("MainPage:ForeignCardTooltip")}>
        <a
          href="https://shop.govoriru.ru/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button type="button" variant="soft">
            {t("Login:BuyTeacherLessons")}
          </Button>
        </a>
      </Tooltip>
    </form>
  );
};
